import 'react-perfect-scrollbar/dist/css/styles.css';
import './index.scss';

import { CssBaseline } from '@material-ui/core';
import { createGenerateClassName, StylesProvider, ThemeProvider } from '@material-ui/styles';
import { store } from '@redux/store';
import cautionIcon from 'assets/images/svg/caution.svg';
import AppProvider, { useAppState } from 'components/AppProvider';
import ErrorBoundary from 'components/common/ErrorBoundary';
import Loader from 'components/Modals/Loader';
import PermissionCheck from 'components/PermissionCheck';
import routes from 'constant/routes';
import SCRIPTS from 'constant/scripts';
import { eventsToDispatch } from 'helpers/events';
import i18n from 'i18next';
import { GenerateId, Rule } from 'jss';
import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import ReactDependentScript from 'react-dependent-script';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { HashRouter, Redirect, Route, Switch } from 'react-router-dom';
import ErrorNavigation from 'ui/ErrorNavigation';
import Theme from 'ui/Theme';

import DispatchLanguage from './assets/locales';

let generateClassName: GenerateId | undefined = undefined;

if (process.env.NODE_ENV === 'production') {
    // use the default class name creator from mui
    generateClassName = createGenerateClassName();
} else {
    // make your own name generator
    const createGenerateId = () => {
        let counter = 0;
        return (rule: Rule, sheet: any) =>
            `${sheet.options.classNamePrefix}-${rule.key}-${counter++}`;
    };
    generateClassName = createGenerateId();
}

const defaultStartPath = 'dispatch';

const MainApp = ({ location }: $TSFixMe) => {
    const [, dispatch] = useAppState();
    const [t] = useTranslation('dispatch');

    const handleUnAuth = () => {
        dispatch({
            type: 'UPDATE_MODAL',
            payload: {
                icon: (
                    <img
                        src={cautionIcon}
                        style={{
                            marginBottom: '15px'
                        }}
                    />
                ),
                open: true,
                type: 'error',
                messageData: {
                    title: '401 Unauthorised',
                    description: (
                        <>
                            <p>Looks like you don&apos;t have access to this</p>
                            <p>
                                Log in to access it or contact your administrator at
                                admin@company.com to give you access to this
                            </p>
                        </>
                    ),
                    confirmText: 'Back to previous page',
                    cancelText: 'Refresh page'
                },
                onConfirm: () => {
                    dispatch({ type: 'UPDATE_MODAL', payload: { open: false } });
                },
                onCancel: () => {
                    dispatch({ type: 'UPDATE_MODAL', payload: { open: false } });
                    window.location.reload();
                }
            }
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener(eventsToDispatch.UNAUTH_ACCESS, handleUnAuth);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener(eventsToDispatch.UNAUTH_ACCESS, handleUnAuth);
            }
        };
    }, []);

    if (location.pathname === '/') {
        return <Redirect to={defaultStartPath} />;
    }

    return (
        <div id="dispatch-ct" style={{ background: '#fff' }}>
            <Switch>
                {routes.items
                    .flatMap((module) => module.tabs)
                    .map((item, index: number) => (
                        <RouteWrapper
                            component={item.component}
                            key={index}
                            name={item.name}
                            path={item.path}
                            layout={item.layout}
                            rightContent={item.rightContent}
                            title={t(item.title || '')}
                            allowedPermissions={item.allowedPermissions}
                        />
                    ))}
                <Route
                    path="*"
                    component={() => {
                        ErrorNavigation.showNotFoundPage();
                        return null;
                    }}
                />
            </Switch>
        </div>
    );
};

// @ts-expect-error ts-migrate(7031) FIXME: Binding element 'Component' implicitly has an 'any... Remove this comment to see the full error message
function RouteWrapper({ component: Component, layout: Layout, allowedPermissions, ...rest }) {
    const handleDeniedPermission = useCallback(() => {
        ErrorNavigation.showForbiddenPage();
    }, []);

    return (
        <Route
            {...rest}
            render={(props: $TSFixMe) => {
                const content = (
                    <Layout {...props} {...rest}>
                        <ErrorBoundary>
                            <Component {...props} />
                        </ErrorBoundary>
                    </Layout>
                );

                return allowedPermissions ? (
                    <PermissionCheck
                        allowedPermissions={allowedPermissions}
                        onDeny={handleDeniedPermission}>
                        {content}
                    </PermissionCheck>
                ) : (
                    content
                );
            }}
        />
    );
}

const ExportedApp = () => {
    // Load i18n resources
    useLayoutEffect(() => {
        i18n.addResourceBundle('en-US', 'dispatch', DispatchLanguage['en-US']);
        i18n.addResourceBundle('es-CO', 'dispatch', DispatchLanguage['es-CO']);
        i18n.addResourceBundle('id-ID', 'dispatch', DispatchLanguage['id-ID']);
        i18n.addResourceBundle('ja-JP', 'dispatch', DispatchLanguage['ja-JP']);
        i18n.addResourceBundle('ko-KR', 'dispatch', DispatchLanguage['ko-KR']);
        i18n.addResourceBundle('ms-MY', 'dispatch', DispatchLanguage['ms-MY']);
        i18n.addResourceBundle('th-TH', 'dispatch', DispatchLanguage['th-TH']);
        i18n.addResourceBundle('vi-VN', 'dispatch', DispatchLanguage['vi-VN']);
        i18n.addResourceBundle('zh-CHS', 'dispatch', DispatchLanguage['zh-CHS']);
        i18n.addResourceBundle('zh-CHT', 'dispatch', DispatchLanguage['zh-CHT']);
    }, []);

    return (
        <Provider store={store}>
            <StylesProvider generateClassName={generateClassName}>
                <ThemeProvider theme={Theme.lightTheme}>
                    <CssBaseline />
                    <ReactDependentScript
                        loadingComponent={<Loader />}
                        scripts={[SCRIPTS.spiderifyMap]}>
                        <AppProvider>
                            <HashRouter>
                                <Switch>
                                    <Route component={MainApp} path="/" />
                                </Switch>
                            </HashRouter>
                        </AppProvider>
                    </ReactDependentScript>
                </ThemeProvider>
            </StylesProvider>
        </Provider>
    );
};

export default ExportedApp;
